import { useProducts, type Product } from '@airship-platform/airship-proposal';
import { resolveProductPrice } from '@airship-platform/proposal-financing-feature/utils';
import { LoadingButton } from '@airship-platform/jam-web/components';
import { useNavigate } from 'react-router';
import {
  AppointmentHistoryDetail,
  useAppointmentHistory,
} from '../../../data-access/appointmentHistory';
import { useAppointmentProposalOptionModifiers } from '../../../data-access/appointmentProposalOptionModifiers';
import { useAppointmentProposalOptionProducts } from '../../../data-access/appointmentProposalOptionProducts';
import { useAppointmentProposalOptions } from '../../../data-access/appointmentProposalOptions';
import { useAppointmentProposals } from '../../../data-access/appointmentProposals';
import ROUTE_PATHS from '../../../routes/paths';

interface SelectProposalButtonProps {
  appointmentId: string;
  appointmentProposalId: string;
}

const SelectProposalButton: React.FC<SelectProposalButtonProps> = ({
  appointmentId,
  appointmentProposalId,
}) => {
  const { options, createAppointmentProposalOption } =
    useAppointmentProposalOptions();
  const {
    createAppointmentProposalOptionModifier,
    getNextSortOrder: getNextModifierSortOrder,
  } = useAppointmentProposalOptionModifiers();
  const {
    createAppointmentProposalOptionProduct,
    getNextSortOrder: getNextProductSortOrder,
  } = useAppointmentProposalOptionProducts();
  const { currentProposal, appendSelectedAppointmentProposalOptionsIds } =
    useAppointmentProposals();
  const {
    currentAppointment,
    isLoadingAppointmentId,
    setLoadingAppointmentId,
    fetchAppointmentDetail,
  } = useAppointmentHistory();
  const { fetchProducts } = useProducts();
  const navigate = useNavigate();

  if (!currentProposal) {
    return null;
  }

  const addOptions = async (template: AppointmentHistoryDetail) => {
    const pricebookIds = [
      ...new Set(template.proposalProducts.map((p) => p.pricebookId)),
    ];
    const pricebookItems = pricebookIds.length
      ? await fetchProducts({ pricebookIds })
      : [];
    const pricebookMap = new Map<string, Product>(
      pricebookItems.map((p) => [p.pricebookId, p])
    );

    const newOptions = await Promise.all(
      template.options.map((option, index) =>
        createAppointmentProposalOption(
          currentProposal.appointmentId,
          currentProposal.appointmentProposalId,
          {
            jobId: currentProposal.jobId,
            optionType: option.optionType,
            name: option.name,
            sortOrder:
              index + Math.max(0, ...options.map((o) => o.sortOrder + 1)),
          }
        ).then((newOption) =>
          Promise.all([
            Promise.all(
              template.proposalProducts
                .filter(
                  (p) =>
                    p.appointmentProposalOptionId ===
                      option.appointmentProposalOptionId &&
                    p.groupingName !== 'LINKED_ITEM'
                )
                .map(
                  (
                    {
                      appointmentProposalOptionProductId:
                        _appointmentProposalOptionProductId,
                      ...product
                    },
                    pIdx
                  ) => {
                    const pricebookItem = pricebookMap.get(product.pricebookId);

                    const requestedPriceType =
                      product.defaultPriceType ??
                      pricebookItem?.defaultPriceType ??
                      'STANDARD';

                    const {
                      priceType,
                      price,
                      linkedItems,
                    } = resolveProductPrice(requestedPriceType, pricebookItem, {
                      price: product.price,
                      currency: product.currency,
                      taxable: product.taxable,
                      linkedItems: product.linkedItems ?? [],
                    });

                    return createAppointmentProposalOptionProduct(
                      currentProposal.appointmentId,
                      currentProposal.appointmentProposalId,
                      newOption.appointmentProposalOptionId,
                      {
                        ...product,
                        defaultPriceType: priceType,
                        price: price.value,
                        currency: price.currency,
                        taxable: price.taxable,
                        pricebookPrices: pricebookItem?.prices ?? [],
                        linkedItems,
                        jobId: currentProposal.jobId,
                        sortOrder: getNextProductSortOrder(pIdx),
                      }
                    );
                  }
                )
            ),
            Promise.all(
              template.proposalModifiers
                .filter(
                  (m) =>
                    m.appointmentProposalOptionId ===
                    option.appointmentProposalOptionId
                )
                .map(
                  (
                    {
                      appointmentProposalOptionModifierId:
                        _appointmentProposalOptionModifierId,
                      ...modifier
                    },
                    mIdx
                  ) =>
                    createAppointmentProposalOptionModifier(
                      currentProposal.appointmentId,
                      currentProposal.appointmentProposalId,
                      newOption.appointmentProposalOptionId,
                      {
                        ...modifier,
                        jobId: currentProposal.jobId,
                        sortOrder: getNextModifierSortOrder(mIdx),
                      }
                    )
                )
            ),
          ]).then(() => newOption)
        )
      )
    );

    appendSelectedAppointmentProposalOptionsIds(
      newOptions.map((o) => o.appointmentProposalOptionId)
    );
  };

  const onCreateProposalClick = async () => {
    setLoadingAppointmentId(appointmentId);

    if (
      currentAppointment &&
      currentAppointment.proposal.appointmentProposalId ===
        appointmentProposalId
    ) {
      await addOptions(currentAppointment);
    } else {
      const template = await fetchAppointmentDetail(
        appointmentId,
        appointmentProposalId
      );
      await addOptions(template);
    }

    navigate(
      ROUTE_PATHS.APPOINTMENT_PATH({
        appointmentId: currentProposal.appointmentId,
        appointmentProposalId: currentProposal.appointmentProposalId,
        path: 'options',
      })
    );

    setLoadingAppointmentId(null);
  };

  return (
    <LoadingButton
      color={'softGold'}
      onClick={onCreateProposalClick}
      disabled={!!isLoadingAppointmentId}
      loading={isLoadingAppointmentId === appointmentId}
    >
      Select Proposal
    </LoadingButton>
  );
};

export default SelectProposalButton;
